import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../_core/trpc";
import HeartScene from "../components/three/HeartScene";
import { useReducedMotion } from "../components/three/scene-utils";
import { Typewriter } from "../components/lux/Countdown";
import { Emojiful, LightRays, PetalField, Reveal, Rule, FilmGrade, useHeroScroll } from "../components/lux/Atmosphere";
import { MusicPlayer } from "../components/lux/MusicPlayer";
import { MusicToggle } from "../components/lux/MusicToggle";
import { Gallery } from "../components/lux/Gallery";
function useVisitorId() {
  const [id, setId] = useState("");
  useEffect(() => {
    const KEY = "mfb_visitor";
    let existing = "";
    try {
      existing = localStorage.getItem(KEY) ?? "";
    } catch {
    }
    if (!existing || existing.length < 8) {
      existing = (crypto.randomUUID?.() ?? `v-${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(
        /-/g,
        ""
      );
      try {
        localStorage.setItem(KEY, existing);
      } catch {
      }
    }
    setId(existing);
  }, []);
  return id;
}
function Experience({
  data,
  introDone = true
}) {
  const reduced = useReducedMotion();
  const visitorId = useVisitorId();
  const scrollRef = useHeroScroll();
  const [phase, setPhase] = useState(reduced ? 4 : 0);
  const [skipped, setSkipped] = useState(false);
  const scheduleRef = useRef([]);
  useEffect(() => {
    if (!introDone) return;
    if (reduced) {
      setPhase(4);
      return;
    }
    scheduleRef.current.push(
      window.setTimeout(() => setPhase(1), 1400),
      window.setTimeout(() => setPhase(2), 2600),
      window.setTimeout(() => setPhase(3), 3800)
    );
    return () => {
      scheduleRef.current.forEach(window.clearTimeout);
      scheduleRef.current = [];
    };
  }, [reduced, introDone]);
  const replay = useCallback(() => {
    if (reduced) return;
    setPhase(0);
    setSkipped(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    [1400, 2600, 3800].forEach(
      (ms, i) => scheduleRef.current.push(window.setTimeout(() => setPhase(i + 1), ms))
    );
  }, [reduced]);
  const letterDone = useCallback(() => setPhase(4), []);
  const giftMessages = data.messages.filter((m) => m.kind === "gift");
  const hiddenMessages = data.messages.filter((m) => m.kind === "hidden");
  const scheduledMessages = data.messages.filter((m) => m.kind === "scheduled");
  const tracks = useMemo(
    () => data.audio.map((a) => ({
      id: a.id,
      url: a.url,
      caption: a.caption,
      mime: a.mime,
      durationSec: a.durationSec
    })),
    [data.audio]
  );
  const musicLocked = data.revealMusicAfterUnlock === false && data.videos.length === 0;
  return <main className="no-hscroll relative min-h-[100svh] overflow-hidden">
      {
    /* Same floating control as the teaser: the soundtrack belongs to the page,
       not to whichever card happens to be on screen. */
  }
      {!musicLocked && <MusicToggle tracks={tracks} />}
      <FilmGrade reduced={reduced} />

      {
    /* ── ambient stage: opening screen only ── */
  }
      <div className="stage-fade pointer-events-none absolute inset-x-0 top-0 z-0 h-[100svh] overflow-hidden">
        <LightRays intensity={data.animationIntensity} reduced={reduced} />
        <HeartScene
    intensity={data.animationIntensity}
    mode="reveal"
    className="absolute inset-0"
    scrollRef={scrollRef}
  />
        <PetalField count={6} />
      </div>
      {
    /* Below the hero the page gets its own ground so text and photos never sit
       on top of a rendered scene. */
  }
      <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-x-0 bottom-0 top-[100svh] z-0"
    style={{
      background: "linear-gradient(180deg, rgba(11,12,22,0) 0%, rgba(11,12,22,.92) 14%, #0b0c16 32%)"
    }}
  />

      {
    /* ── STAGE 2: the reveal ── */
  }
      <section className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-3xl flex-col items-center justify-center px-5 py-20 text-center sm:px-8">
        <p
    className="eyebrow"
    style={{
      opacity: phase >= 1 ? 1 : 0,
      transition: "opacity 1.2s ease"
    }}
  >
          {data.subtitles[0] ?? "Today is yours"}
        </p>

        <h1
    className="display-xl gold-text mt-6 max-w-3xl"
    style={{
      opacity: phase >= 2 ? 1 : 0,
      transform: phase >= 2 ? "none" : "translateY(26px) scale(.97)",
      transition: "opacity 1.4s cubic-bezier(.16,1,.3,1), transform 1.4s cubic-bezier(.16,1,.3,1)"
    }}
  >
          <Emojiful text={data.mainHeading} />
        </h1>

        {data.subtitles.length > 1 && <div
    className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2"
    style={{ opacity: phase >= 2 ? 1 : 0, transition: "opacity 1.6s ease .4s" }}
  >
            {data.subtitles.slice(1).map((s, i) => <span
    key={i}
    className="text-[0.7rem] uppercase tracking-[0.28em] text-[color:var(--muted-foreground)]"
  >
                {s}
              </span>)}
          </div>}

        <Rule className="my-10 w-40" />

        {
    /* The letter itself. Kept in a glass panel so the typewriter has a stable
       backdrop — text over a moving 3D scene is unreadable otherwise. */
  }
        <div
    className="glass gold-frame w-full max-w-2xl rounded-2xl px-5 py-8 text-left sm:px-9 sm:py-10"
    style={{
      opacity: phase >= 3 ? 1 : 0,
      transform: phase >= 3 ? "none" : "translateY(22px)",
      transition: "opacity 1.2s ease, transform 1.2s cubic-bezier(.16,1,.3,1)"
    }}
  >
          {phase >= 3 ? <Typewriter
    text={data.letterBody}
    reducedMotion={reduced || skipped}
    onDone={letterDone}
    speed={11}
    className="prose-romance whitespace-pre-line"
    as="div"
  /> : <p className="prose-romance opacity-0">·</p>}

          <div className="mt-8 flex items-center gap-3">
            <span className="gold-rule h-px flex-1" />
            <span className="font-display text-sm italic text-[color:var(--gold-dim)]">
              — {data.myNickname}
            </span>
          </div>
          {
    /* Bottom breathing room to match the panel's top padding, so the
       signature does not sit hard against the frame. */
  }
          <div className="h-2" />
          {phase === 3 && !skipped && <button
    type="button"
    data-testid="skip-letter-btn"
    onClick={() => setSkipped(true)}
    className="absolute bottom-3 right-4 rounded-full px-3 py-2 text-[0.68rem] uppercase tracking-[0.18em] text-[color:var(--gold-dim)] transition-colors hover:text-[#e9a8f2]"
  >
              Show it all
            </button>}
        </div>

        {
    /* Play-the-song: the ONLY way audio starts. Never autoplayed. */
  }
        {phase >= 4 && tracks.length > 0 && <div className="anim-fade-up mt-8 w-full max-w-xl">
            <MusicPlayer
    tracks={tracks}
    title="Your birthday song"
    subtitle="Press play when you're ready"
  />
          </div>}

        <div
    className="mt-10 flex flex-col items-center gap-3 sm:flex-row"
    style={{ opacity: phase >= 4 ? 1 : 0, pointerEvents: phase >= 4 ? "auto" : "none", transition: "opacity 1.2s ease .3s" }}
    aria-hidden={phase >= 4 ? void 0 : true}
  >
          <a data-testid="open-memories-btn" href="#memories" className="btn-gold">
            Open your memories
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </a>
          <button data-testid="replay-btn" type="button" onClick={replay} className="btn-ghost-lux" disabled={reduced}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
    d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
  />
            </svg>
            Replay
          </button>
        </div>
      </section>

      {
    /* ── STAGE 3: memories & gifts ── */
  }
      <div id="memories" className="relative z-10 mx-auto w-full max-w-5xl px-5 pb-4 sm:px-8">
        {scheduledMessages.length > 0 && <section className="mb-20">
            <div className="mb-8 text-center">
              <p className="eyebrow">Notes that arrived with the day</p>
              <h2 className="display-md mt-3 rose-text">A few things I wanted to say</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {scheduledMessages.map((m, i) => <Reveal key={m.id} delay={i * 90} reduced={reduced}>
                  <article className="glass h-full rounded-2xl p-6">
                    <p className="eyebrow">{m.title}</p>
                    <p className="prose-romance mt-3 whitespace-pre-line">{m.body}</p>
                  </article>
                </Reveal>)}
            </div>
          </section>}

        {data.photos.length > 0 && <section className="mb-20">
            <div className="mb-8 text-center">
              <p className="eyebrow">Us, in pictures</p>
              <h2 className="display-md mt-3 gold-text">Every frame I kept</h2>
            </div>
            <Gallery
    photos={data.photos}
    layout={data.galleryLayout}
    zoomEffect={data.zoomEffect}
    reduced={reduced}
  />
          </section>}

        {data.videos.length > 0 && <section className="mb-20">
            <div className="mb-8 text-center">
              <p className="eyebrow">A little film</p>
              <h2 className="display-md mt-3 rose-text">Press play, my love</h2>
            </div>
            <div className="space-y-6">
              {data.videos.map((v) => <Reveal key={v.id} reduced={reduced}>
                  <figure className="glass gold-frame mx-auto max-w-2xl overflow-hidden rounded-2xl p-2">
                    {
    /* playsInline + no autoplay: iOS would otherwise hijack the
       screen full-bleed the instant the page loads. */
  }
                    <video
    src={v.url}
    poster={v.posterUrl ?? void 0}
    controls
    playsInline
    preload="none"
    aria-label={v.caption ?? "The birthday video"}
    className="mx-auto block max-h-[76svh] w-full rounded-xl bg-black object-contain"
  />
                    {v.caption && <figcaption className="px-3 py-3 text-center text-sm text-[color:var(--muted-foreground)]">
                        {v.caption}
                      </figcaption>}
                  </figure>
                </Reveal>)}
            </div>
          </section>}

        {(giftMessages.length > 0 || hiddenMessages.length > 0) && <section className="mb-20">
            <div className="mb-8 text-center">
              <p className="eyebrow">One more thing</p>
              <h2 className="display-md mt-3 gold-text">
                {giftMessages.length > 0 ? "A gift, wrapped in words" : "Hidden notes"}
              </h2>
            </div>
            {
    /* `justify-center` + auto-fit: a lone gift card used to sit in the
       left column with an empty cell beside it, which read as a missing
       item rather than a deliberate single gift. */
  }
            <div className="grid grid-cols-1 gap-5 pb-16 sm:grid-cols-2">
              {giftMessages.map((m) => <GiftBox key={m.id} message={m} visitorId={visitorId} reduced={reduced} />)}
              {hiddenMessages.map((m) => <HiddenNote key={m.id} message={m} visitorId={visitorId} />)}
            </div>
          </section>}

        {
    /* Final message */
  }
        {data.finalMessage && <Reveal reduced={reduced}>
            <section className="relative overflow-hidden rounded-3xl px-5 py-16 text-center sm:px-10">
              <div className="pointer-events-none absolute inset-0">
                <PetalField count={4} />
              </div>
              <div className="relative">
                <div className="mx-auto mb-8 h-16 w-16">
                  <HeartScene intensity={1} mode="reveal" className="h-full w-full" />
                </div>
                <p className="prose-romance mx-auto max-w-2xl text-balance">{data.finalMessage}</p>
                <Rule className="mx-auto my-8 w-28" />
                <p className="font-display text-lg italic text-[color:var(--gold-dim)]">
                  Forever yours, {data.myNickname}
                </p>
              </div>
            </section>
          </Reveal>}

        <div className="mt-10 flex justify-center">
          <button data-testid="watch-again-btn" type="button" onClick={replay} className="btn-ghost-lux" disabled={reduced}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
    d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
  />
            </svg>
            Watch it again
          </button>
        </div>
      </div>
    </main>;
}
function GiftBox({
  message,
  visitorId,
  reduced
}) {
  const [open, setOpen] = useState(false);
  const mark = trpc.public.markRevealed.useMutation();
  const reveals = trpc.public.reveals.useQuery(
    { visitorId },
    { enabled: Boolean(visitorId), staleTime: 6e4 }
  );
  useEffect(() => {
    if (reveals.data?.ids?.includes(message.id)) setOpen(true);
  }, [reveals.data, message.id]);
  const openIt = () => {
    setOpen(true);
    if (visitorId) mark.mutate({ visitorId, messageId: message.id });
  };
  return <div className="relative">
      {!open ? <button
    type="button"
    onClick={openIt}
    className="glass gold-frame group relative flex h-full w-full flex-col items-center justify-center gap-4 rounded-2xl px-6 py-14 transition hover:border-[rgba(233,168,242,.45)]"
    data-testid={`gift-box-${message.id}`}
    aria-label={`Open the gift: ${message.title}`}
  >
          <span className="relative grid h-20 w-20 place-items-center">
            {
    /* pulsing halo — pure CSS so it costs nothing on mobile */
  }
            <span
    aria-hidden="true"
    className="absolute inset-0 rounded-full border border-[rgba(233,168,242,.5)]"
    style={{ animation: reduced ? void 0 : "lux-pulse-ring 2.6s ease-out infinite" }}
  />
            <svg width="46" height="46" viewBox="0 0 24 24" aria-hidden="true">
              <path
    d="M3 9h18v12H3zM2 6.5h20V9H2zM12 9v12M12 6.5c-1.6-3.4-6-3.1-6-.1 0 1.6 1.8 2.3 3.5 2.1M12 6.5c1.6-3.4 6-3.1 6-.1 0 1.6-1.8 2.3-3.5 2.1"
    stroke="#e9a8f2"
    strokeWidth="1.15"
    fill="rgba(233,168,242,.08)"
    strokeLinejoin="round"
  />
            </svg>
          </span>
          <span className="text-[0.68rem] uppercase tracking-[0.28em] text-[color:var(--gold-dim)]">
            Tap to open
          </span>
          <span className="font-display text-lg text-[#f3f4f9]">{message.title}</span>
        </button> : <article data-testid={`gift-open-${message.id}`} className="glass gold-frame anim-fade-up h-full rounded-2xl px-6 py-8">
          <p className="eyebrow">{message.title}</p>
          <p className="prose-romance mt-3 whitespace-pre-line">{message.body}</p>
        </article>}
    </div>;
}
function HiddenNote({
  message,
  visitorId
}) {
  const [open, setOpen] = useState(false);
  const mark = trpc.public.markRevealed.useMutation();
  const reveals = trpc.public.reveals.useQuery(
    { visitorId },
    { enabled: Boolean(visitorId), staleTime: 6e4 }
  );
  useEffect(() => {
    if (reveals.data?.ids?.includes(message.id)) setOpen(true);
  }, [reveals.data, message.id]);
  const openIt = () => {
    setOpen(true);
    if (visitorId) mark.mutate({ visitorId, messageId: message.id });
  };
  return <div className="relative">
      {!open ? <button
    type="button"
    onClick={openIt}
    className="glass-soft flex h-full w-full flex-col items-start gap-2 rounded-2xl px-6 py-10 text-left transition hover:border-[rgba(233,168,242,.4)]"
    data-testid={`hidden-note-${message.id}`}
    aria-label={`Reveal the note: ${message.title}`}
  >
          <span className="text-[0.68rem] uppercase tracking-[0.28em] text-[color:var(--gold-dim)]">
            Sealed
          </span>
          <span className="font-display text-lg text-[#f3f4f9]">{message.title}</span>
          <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-[color:var(--muted-foreground)]">
            Tap to read
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
        </button> : <article data-testid={`hidden-note-open-${message.id}`} className="glass anim-fade-up h-full rounded-2xl px-6 py-6">
          <p className="eyebrow">{message.title}</p>
          <p className="prose-romance mt-3 whitespace-pre-line">{message.body}</p>
        </article>}
    </div>;
}
export {
  Experience as default
};
